import {
  generatePreviousComparisonValues,
  generateBenchmarkComparisonValues,
  arrayRange,
} from "../../../shared/utils";

/**
 * Workflow utilities - Pure helper functions for workflow calculations
 * Extracted from snapshotWorkflows.js for reusability and testability
 */

// Map position numbers to their count field names
export const targetPositionCountMap = new Map([
  [0, "zero_position_count"],
  [1, "one_position_count"],
  [2, "two_position_count"],
  [3, "three_position_count"],
  [4, "four_position_count"],
  [5, "five_position_count"],
  [6, "six_position_count"],
]);

// Map comparison types to their benchmark field names
export const workflowDiffOptionsMap = new Map([
  ["vs_prev", "prev_percentage"],
  ["vs_org", "org_score"],
  ["vs_50th", "benchmark_50"],
  ["vs_75th", "benchmark_75"],
  ["vs_90th", "benchmark_90"],
]);

/**
 * Generate comparison values for chart overlays
 * @param {Object} resource - Resource with values array and benchmark data
 * @param {string} benchmark - Benchmark type (vs_prev, vs_50th, etc.)
 * @param {string} resourceType - Type of resource (workflow, factor)
 * @returns {Array} Comparison values for chart overlay
 */
export function generateComparisonValues(
  resource,
  benchmark,
  resourceType = "workflow",
) {
  let comparisonValues = [];

  if (
    ["vs_prev", "prev_percentage"].includes(benchmark) &&
    resource.values.length > 1
  ) {
    comparisonValues = generatePreviousComparisonValues(resource);
  } else if (!["vs_prev", "prev_percentage"].includes(benchmark)) {
    comparisonValues = generateBenchmarkComparisonValues(
      resource,
      resourceType,
      benchmark,
    );
  }

  return comparisonValues;
}

/**
 * Calculate percentage width for a position bar
 * @param {Object} workflow - Workflow data object
 * @param {number} position - Position number (0-6)
 * @returns {string} CSS width property value
 */
export function percentageWidthForPosition(workflow, position) {
  return `width: ${workflowPercentageOfResponses(workflow, position)}`;
}

/**
 * Get response count for a specific position
 * @param {Object} workflow - Workflow data object
 * @param {number} position - Position number (0-6)
 * @returns {number} Count of responses at this position
 */
export function countForPosition(workflow, position) {
  return workflow[targetPositionCountMap.get(position)];
}

/**
 * Calculate percentage of responses at a position
 * @param {Object} workflow - Workflow data object
 * @param {number} position - Position number (0-6)
 * @returns {string} Formatted percentage string (e.g., "42%")
 */
export function workflowPercentageOfResponses(workflow, position) {
  if (workflow["count"] === 0) return "0%";

  return `${Math.round(
    (100 * workflow[targetPositionCountMap.get(position)]) / workflow["count"],
  )}%`;
}

/**
 * Get array of positions in the target range
 * @param {Object} workflow - Workflow data with target_direction and target_position
 * @param {number} maxBehavioralQuestions - Max number of question options
 * @returns {Array<number>} Array of position numbers in target range
 */
export function targetedPositions(workflow, maxBehavioralQuestions) {
  if (!workflow) return;

  if (workflow.target_direction == "u")
    return arrayRange(0, workflow.target_position, 1);

  return arrayRange(
    workflow.target_position,
    maxBehavioralQuestions - 1,
    1,
  ).reverse();
}

/**
 * Get array of positions outside the target range
 * @param {Object} workflow - Workflow data with target_direction and target_position
 * @param {number} maxBehavioralQuestions - Max number of question options
 * @returns {Array<number>} Array of position numbers outside target range
 */
export function nonTargetedPositions(workflow, maxBehavioralQuestions) {
  if (!workflow) return;

  if (workflow.target_direction == "u")
    return arrayRange(
      workflow.target_position + 1,
      maxBehavioralQuestions - 1,
      1,
    );

  return arrayRange(0, workflow.target_position - 1, 1).reverse();
}

/**
 * Calculate percentage of responses in a given field
 * @param {Object} item - Data item
 * @param {string} positionCount - Field name for position count
 * @param {string} totalFieldCount - Field name for total count
 * @returns {string} Formatted percentage string or undefined
 */
export function percentageOfResponses(item, positionCount, totalFieldCount) {
  if (!item) return;

  if (item[totalFieldCount] === 0) return "0%";

  return `${Math.round((100 * item[positionCount]) / item[totalFieldCount])}%`;
}

/**
 * Calculate benchmark difference for workflow comparison
 * @param {Object} workflow - Workflow data
 * @param {string} compareTo - Comparison type
 * @returns {number|null} Difference value or null if not applicable
 */
export function workflowBenchmarkDiff(workflow, compareTo) {
  if (
    ["vs_50th", "vs_75th", "vs_90th"].includes(compareTo) &&
    workflow["benchmark_50"] === null
  )
    return null;
  if (workflow.score == null) return null;
  if (workflow.prev_percentage == null && compareTo == "vs_prev") return null;
  if (workflow.org_percentage == null && compareTo == "vs_org") return null;

  return (
    Math.round(workflow.score) -
    Math.round(workflow[workflowDiffOptionsMap.get(compareTo)])
  );
}
